import { Injectable } from '@angular/core';
import { DataResult, DataRow } from '../pages/view/view.page';
import * as XLSX from 'xlsx-with-styles';

export interface DataReceiver {
  addRows: (rows: DataRow[]) => void
  deleteIndex: (index: number) => void
  overwriteIndex: (data: DataRow, index: number) => void
  getAllData: () => DataRow[]
  getResultData: () => DataResult
  getDataHeaders: () => string[]
  clearWip: () => void
}

@Injectable({
  providedIn: 'root'
})
export class DataService {
  public receiver: DataReceiver

  constructor() { }

  public readFiles(files: FileList) {
    console.log(files);
    for (let i = 0; i < files.length; i++) {
      if (files[i].name.endsWith('.csv')) {
        this.readCSV(files[i]);
      } else if (files[i].name.endsWith('.xlsx')) {
        this.readXLSX(files[i]);
      } else {
        console.error('unsupported file type');
      }
    }
    this.saveLastImport();
  }

  async readXLSX(file: File) {
    // Convert the File into an ArrayBuffer (required by the library)
    const arrayBuffer = await file.arrayBuffer();
    const workbook = XLSX.read(arrayBuffer, { type: 'array' });
    const wsName = workbook.SheetNames[0];
    const worksheet = workbook.Sheets[wsName];
    this.parseXLS(XLSX.utils.sheet_to_json(worksheet, { header: 1 }), file.name)
  }

  parseXLS(xlsRows, fileName) {
    let data: DataRow[] = [];
    let format: 'elba' | 'ggdl' = null;
    let stop = false;
    let header = false;

    // go through data to detect file format
    let nonStrings = 0;
    xlsRows[0].forEach(e => {
      if (typeof e !== "string") {
        nonStrings++;
      }
    });
    if (nonStrings == 0) {
      // no numbers in first row indicates all string headers
      format = 'ggdl';
      header = true;
    } else {
      // numbers in first row indicates a data row
      format = 'elba';
    }

    xlsRows.forEach(row => {
      if (format == 'ggdl') {
        // GGDL import
        if (!header) {
          // stop when reading an empty line, signaling the end of entry data
          if (row.length == 0) {
            stop = true;
          }
          if (!stop) {
            console.log('ggdl', row)
            data.push({
              entry: {
                date: this.excelSerialToDate(row[0]),
                text: row[2],
                brutto: row[3],
                konto: row[8],
                note: row[1],
                st_percentage: row[4] * 100
              }
            });
          }
        } else {
          header = false;
        }
      } else if (format == 'elba') {
        // ELBA import
        console.log('elba', row)

        data.push({
          entry: {
            date: this.excelSerialToDate(row[0]),
            text: row[1],
            brutto: row[3],
            konto: fileName.replace('UTF-8\'\'',''),
            note: '',
            st_percentage: 0
          }
        });
      }
    });
    this.receiver.addRows(data);
  }

  excelSerialToDate(excelSerial: number): Date {
    // Excel's epoch starts at 1899‑12‑30 (the “zero” day).
    const EXCEL_EPOCH = new Date(Date.UTC(1899, 11, 30));

    // Whole days part
    const days = Math.floor(excelSerial);
    // Fractional day part → milliseconds
    const fraction = excelSerial - days;
    const msInDay = 24 * 60 * 60 * 1000;

    // Build the final timestamp
    const resultMs = EXCEL_EPOCH.getTime() + days * msInDay + fraction * msInDay;
    return new Date(resultMs);
  }

  readCSV(file) {
    let fileReader = new FileReader();
    console.log(file);
    fileReader.onload = (e) => {
      this.parseCSV(fileReader.result as string, file.name);
    }
    fileReader.readAsText(file, "ISO-8859-1")
  }

  parseCSV(text: string, filename: string) {
    console.log(text)
    let data: DataRow[] = [];
    let rows: string[] = text.split('\n');

    rows.forEach(r => {
      if (r.length > 0) {
        let cols: string[] = r.split(';');

        let date = new Date();
        let date_split: string[] = cols[0].split('.')
        date.setDate(+date_split[0])
        date.setMonth((+date_split[1]) - 1)
        date.setFullYear(+date_split[2])

        data.push({
          entry: {
            date: date,
            text: cols[1].split('\"').join(''),
            brutto: +(cols[3].replace(',', '.')),
            konto: filename,
            note: '',
            st_percentage: 0
          }
        });
      }

    })
    this.receiver.addRows(data);
  }

  public downloadData() {
    // todo either download csv or xlsx
    this.downloadXlsx();
    // this.downloadCsv();
  }

  private getCsv(xlsCompatible = false): string {
    let str = '';

    this.receiver.getDataHeaders().forEach((h, i) => {
      if (i > 0) { // exclude "checked" property from export
        str += h + ';';
      }
    });
    str += '\n';

    this.receiver.getAllData().forEach(r => {
      let str_row = '';

      str_row += xlsCompatible ? r.entry.date + ';' : r.entry.date.getUTCDate() + '.' + (r.entry.date.getUTCMonth() + 1) + '.' + r.entry.date.getUTCFullYear() + ';';
      str_row += r.entry.note + ';';
      str_row += r.entry.text + ';';
      str_row += xlsCompatible ? (r.entry.brutto + ';') : (r.entry.brutto + ';').replace('.', ',');
      str_row += xlsCompatible ? (r.entry.st_percentage / 100 + ';') : (r.entry.st_percentage / 100 + ';').replace('.', ',');
      str_row += xlsCompatible ? (r.calculated.netto + ';') : (r.calculated.netto + ';').replace('.', ',');
      str_row += xlsCompatible ? (r.calculated.st_value + ';') : (r.calculated.st_value + ';').replace('.', ',');
      str_row += r.calculated.vst_or_ust + ';';
      str_row += r.entry.konto + ';';

      // combine with other data rows
      str += str_row + '\n';
    });

    // summary rows
    let r = this.receiver.getResultData();
    let r_str = '';
    r_str += '\n\n';
    r_str += ';;brutto;netto;;Text;Konto;Betrag\n';
    r_str += 'Einnahmen;0%;' + r.brutto_in_0 + ';' + r.netto_in_0 + ';;Umsatzsteuer;3500;' + r.ust + '\n';
    r_str += 'Einnahmen;10%;' + r.brutto_in_10 + ';' + r.netto_in_10 + ';Nr 029;Vorsteuer;2500;' + r.vst + ';Nr 060\n';
    r_str += 'Einnahmen;20%;' + r.brutto_in_20 + ';' + r.netto_in_20 + ';Nr 022;MWSt-Zahllast;3520;' + r.MWSt_Zahllast + '\n';
    r_str += 'GESAMT;;' + r.brutto_in_total + ';' + r.netto_in_total + '\n\n';
    r_str += ';;Umsätze;' + r.tax_revenue + ';Nr 000'; // todo is this really just the taxed umsätze?
    str += xlsCompatible ? r_str : r_str.split('.').join(',');
    return str;
  }

  private downloadXlsx() {
    let csv = this.getCsv(true);
    let lines = csv.split('\n').length; // brutto einnahmen += -5

    // parse csv into a workbook object
    const wb = XLSX.read(csv, { type: "string", cellDates: true });
    // get the worksheet (default name "Sheet1")
    const ws = wb.Sheets.Sheet1;

    // widths
    ws['!cols'] = [{ width: 10 }, { width: 30 }, { width: 70 }, { width: 15 }, { width: 10 }, { width: 15 }, { width: 15 }, { width: 10 }, { width: 50 },]

    // netto formulae
    for (let i = 0; i < lines - 10; i++) {
      let line = 2 + i;
      let cell = 'F' + line;
      ws[cell].f = 'D' + line + '/(1+E' + line + ')';
    }

    // st in eur formulae
    for (let i = 0; i < lines - 10; i++) {
      let line = 2 + i;
      let cell = 'G' + line;
      ws[cell].f = 'F' + line + ' * E' + line;
    }

    // ust vst formulae
    for (let i = 0; i < lines - 10; i++) {
      let line = 2 + i;
      let cell = 'H' + line;
      ws[cell].f = 'IF(D' + line + '>0,"Ust",IF(D' + line + '<0,"Vst",""))';
    }


    // result formulae
    ws['C' + (lines - 5)].f = 'SUMIFS(D2:D' + (lines - 9) + ',E2:E' + (lines - 9) + ',B' + (lines - 5) + ',D2:D' + (lines - 9) + ',">0")' // brutto 0
    ws['C' + (lines - 4)].f = 'SUMIFS(D2:D' + (lines - 9) + ',E2:E' + (lines - 9) + ',B' + (lines - 4) + ',D2:D' + (lines - 9) + ',">0")' // brutto 10
    ws['C' + (lines - 3)].f = 'SUMIFS(D2:D' + (lines - 9) + ',E2:E' + (lines - 9) + ',B' + (lines - 3) + ',D2:D' + (lines - 9) + ',">0")' // brutto 20
    ws['C' + (lines - 2)].f = 'SUM(C' + (lines - 5) + ':C' + (lines - 3) + ')' // total

    ws['D' + (lines - 5)].f = 'SUMIFS(F2:F' + (lines - 9) + ',E2:E' + (lines - 9) + ',B' + (lines - 5) + ',F2:F' + (lines - 9) + ',">0")' // netto 0
    ws['D' + (lines - 4)].f = 'SUMIFS(F2:F' + (lines - 9) + ',E2:E' + (lines - 9) + ',B' + (lines - 4) + ',F2:F' + (lines - 9) + ',">0")' // netto 10
    ws['D' + (lines - 3)].f = 'SUMIFS(F2:F' + (lines - 9) + ',E2:E' + (lines - 9) + ',B' + (lines - 3) + ',F2:F' + (lines - 9) + ',">0")' // netto 20
    ws['D' + (lines - 2)].f = 'SUM(D' + (lines - 5) + ':D' + (lines - 3) + ')' // total

    ws['H' + (lines - 5)].f = 'SUMIF(G2:G' + (lines - 9) + ',">0",G2:G' + (lines - 9) + ')' // ust
    ws['H' + (lines - 4)].f = 'SUMIF(G2:G' + (lines - 9) + ',"<0",G2:G' + (lines - 9) + ')' // vst
    ws['H' + (lines - 3)].f = 'SUM(H' + (lines - 5) + ':H' + (lines - 4) + ')' // mwst zahll

    ws['D' + lines].f = 'SUM(D' + (lines - 4) + ':D' + (lines - 3) + ')' // umsaetze

    // currency formats
    this.XlsCurrencyColoumnFormat(ws, 'D', lines);
    this.XlsCurrencyColoumnFormat(ws, 'F', lines);
    this.XlsCurrencyColoumnFormat(ws, 'G', lines);

    for (let i = lines - 5; i <= lines - 2; i++) {
      // brutto & netto result
      ws['C' + i].z = '€ ###,###,##0.00';
      ws['D' + i].z = '€ ###,###,##0.00';
    }
    for (let i = lines - 5; i <= lines - 3; i++) {
      // ust, vst, mwst zahll. result
      ws['H' + i].z = '€ ###,###,##0.00';
    }
    ws['D' + (lines)].z = '€ ###,###,##0.00';

    // percentage formats
    for (let i = 0; i < lines - 10; i++) {
      // rows
      let cell = 'E' + (2 + i);
      ws[cell].z = '#0 %';
    }

    for (let i = lines - 5; i <= lines - 3; i++) {
      // result
      let cell = 'B' + i;
      ws[cell].z = '#0 %';
    }

    // date formats and fixes
    for (let i = 0; i < lines - 10; i++) {
      let cell = 'A' + (2 + i);
      ws[cell].z = 'dd.mm.yyyy';
    }

    // autofilter
    ws['!autofilter'] = { ref: '1:1' };

    // header style
    const headerStyle = {
      fill: {
        patternType: "solid", // none / solid
        fgColor: { rgb: "FFDCDCDC" }
      },
      font: {
        bold: true
      }
    };
    this.XlsApplyHeaderStyle(ws, headerStyle);

    // result code styles
    const resultCodeStyle = {
      font: {
        color: { rgb: "FFFF0000" },
        bold: true,
      }
    };
    ws['E' + (lines - 4)].s = resultCodeStyle;
    ws['I' + (lines - 4)].s = resultCodeStyle;
    ws['E' + (lines - 3)].s = resultCodeStyle;
    ws['E' + (lines - 0)].s = resultCodeStyle;

    // result code-field styles
    const resultCodeFieldStyle = {
      fill: {
        patternType: "solid",
        fgColor: { rgb: "FFFFFF00" }
      }
    };
    ws['D' + (lines - 4)].s = resultCodeFieldStyle;
    ws['H' + (lines - 4)].s = resultCodeFieldStyle;
    ws['D' + (lines - 3)].s = resultCodeFieldStyle;
    ws['D' + (lines - 0)].s = resultCodeFieldStyle;

    // download excel file
    XLSX.writeFile(wb, 'uva.xlsx');
  }

  private XlsApplyHeaderStyle(ws, style) {
    let cols = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I'];
    cols.forEach((c) => { ws[c + '1'].s = style; });
  }

  private XlsCurrencyColoumnFormat(ws: XLSX.WorkSheet, coloumn: string, lines: number) {
    for (let i = 0; i < lines - 10; i++) {
      let cell = coloumn + (2 + i);
      ws[cell].z = '€ ###,###,##0.00';
    }
  }

  // todo add CSV download option
  private downloadCsv() {
    var link = document.createElement('a');
    link.download = 'uva.csv';
    var blob = new Blob([this.getCsv()], { type: 'text/plain' });
    link.href = window.URL.createObjectURL(blob);
    link.click();
  }

  public saveWip() {
    // clone data
    let data = this.receiver.getAllData();
    let serial: any = JSON.parse(JSON.stringify(data));

    serial.forEach((row, index) => {
      // serialize date manually
      row.entry.date = data[index].entry.date.toISOString();
    })
    localStorage.setItem('wip', JSON.stringify(serial));
  }

  public saveLastImport() {
    // clone data
    let data = this.receiver.getAllData();
    let serial: any = JSON.parse(JSON.stringify(data));

    serial.forEach((row, index) => {
      // serialize date manually
      row.entry.date = data[index].entry.date.toISOString();
    })
    localStorage.setItem('lastimport', JSON.stringify(serial));
  }

  public loadLastImport() {
    let lastImportData: DataRow[] = null;
    let data = localStorage.getItem('lastimport');
    if (data) {
      let deserial: any[] = JSON.parse(data);
      deserial.forEach(row => {
        row.entry.date = new Date(row.entry.date);
      })
      lastImportData = deserial as DataRow[];
    } else {
      lastImportData = [];
    }
    this.receiver.addRows(lastImportData);
  }

  public loadWip(): DataRow[] {
    let data = localStorage.getItem('wip');
    if (data) {
      let deserial: any[] = JSON.parse(data);
      deserial.forEach(row => {
        row.entry.date = new Date(row.entry.date);
      })
      return deserial as DataRow[];
    } else {
      return [];
    }
  }

  public clearWip() {
    localStorage.setItem('wip', JSON.stringify([]));
    this.receiver.clearWip();
  }
}
